
import _ from "lodash";
import rest from "@/rest";
import { Component, Vue, Watch, Prop, PropSync } from "vue-property-decorator";
import { Action, namespace } from "vuex-class";
import { List } from "linq-collections";
import { vuex } from "@/store";
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
// component
import TusFileDrop from "@/components/Shared/TusFileDrop.vue";
import ImageAnnotationCanvas from "@/components/UserManagement/ImageAnnotationCanvas.vue";
// interfaces
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { ExBundleVM } from "@/interfaces/ExBundleAssignmentGroupVM";
import { PortalUserVM } from "@/interfaces/PortalUserVM";
import { UserCertificateVm } from "@/interfaces/UserCertificateVm";
import L3DatePicker from "../Shared/L3DatePicker.vue";

//const auth = namespace("auth");
// const globals = namespace("globals");
const globals = namespace("globals");


@Component({
  components: {
    TusFileDrop,
    L3DatePicker,
    ImageAnnotationCanvas
  }
})
export default class CertificateImportDlg extends Vue {
  @PropSync("showDialog") syncedShowDialog!: boolean;
  @Prop() user!: PortalUserVM;
  @Prop() certificate!: UserCertificateVm | null;

  @globals.Getter selectedBundle: any;
  busyOnOcr = false;
  busy = false;
  isValidDate = false;
  isValidCertificate = false;
  userCertificate: UserCertificateVm | null = null;

  mounted() {
    this.updateCertificateValidity();
  }

  @Watch("userCertificate", { deep: true })
  onUserCertificateChanged() {
    this.updateCertificateValidity();
  }

  @Watch("isValidDate")
  onIsValidDateChanged() {
    this.updateCertificateValidity();
  }

  updateCertificateValidity(): void {
    const titleValid = !!this.userCertificate?.title && this.userCertificate.title.trim().length >= 5;
    const summaryValid = (this.userCertificate?.summary?.length ?? 0) <= 300;
    this.isValidCertificate = !!this.userCertificate && titleValid && summaryValid && this.isValidDate;
  }

  @Watch("syncedShowDialog")
  async onShowDialogChanged(val: boolean) {
    if (this.syncedShowDialog) {
      // On dialog open
      if (this.certificate) {
        this.userCertificate = null;
        await this.$globalHelper.delay(10);
        this.userCertificate = _.cloneDeep(this.certificate);
      } else {
        this.userCertificate = <UserCertificateVm>{
          id: 0,
          title: null,
          summary: null,
          userId: this.user.id!,
          firstName: this.user.firstName ?? "",
          lastName: this.user.lastName ?? "",
          exBundleId: 0,
          bundleName: null,
          cat1Name: null,
          cat2Name: null,
          cat3Name: null,
          comment: null,
          sourceFile: null,
          finishedByAdmin: false,
          finishedByAdminName: null,
          closingDate: new Date(),
          signature: null,
          pdfFileName: null,
          pdfPreviewImageUrl: null,
          redactionRectangles: []
        };
      }

      // Disable body scroll on iOS
      this.$nextTick(async () => {
        await this.$globalHelper.delay(10);
        const modal = document.querySelector('.modal');
        disableBodyScroll(modal);
      });
    } else {
      // Dialog closing
      clearAllBodyScrollLocks();
    }
  }

  beforeDestroy() {
    clearAllBodyScrollLocks();
  }

  async onCertificateFileUploaded(response: any) {
    if (!response.success) {
      this.$store.commit("ux/SB_FAILURE", {
        message: response.message,
        timeout: 0
      });
      return;
    }
    if (!this.userCertificate) {
      return;
    }
    this.busy = true;

    if (this.userCertificate?.pdfFileName) {
      await rest.url("orgAdmin/removeUnusedPdfCertificate").post(this.userCertificate);
    }

    this.userCertificate.pdfFileName = response.fileName;
    try {
      const apiResponse = await rest.url("orgAdmin/processUserCertificate").post(this.userCertificate);
      this.userCertificate = apiResponse;
      // Keep the existing valid date and only re-check the form state after the PDF upload.
      this.updateCertificateValidity();
      // Set focus to the title input after processing the certificate.
      this.$nextTick(() => {
        const titleInput = document.querySelector<HTMLInputElement>('#title');
        if (titleInput) {
          titleInput.focus();
        }
      });
    } catch (err) {
      // Error processing certificate - error notification handled by parent
    } finally {
      this.busy = false;
    }
  }

  async onOcrRequested() {
      if (!this.userCertificate) {
      return;
    }
    this.busyOnOcr = true;

    try {
      const apiResponse = await rest.url("orgAdmin/performCertificateOcr").post(this.userCertificate);
      this.userCertificate = apiResponse;
      await this.$nextTick();
      const summaryField = this.$refs.summaryField as (Vue & { validate: (force?: boolean) => boolean }) | undefined;
      summaryField?.validate(true);
    } catch (err) {
      // Error performing OCR - error notification handled by parent
    } finally {
      this.busyOnOcr = false;
    }
  }

  async onSave() {
    // Implement save logic here
    if (!this.userCertificate) {
      return;
    }
    this.busy = true;
    try {
      await rest.url("orgAdmin/saveUserCertificate").post(this.userCertificate);
      this.$emit('update:certificateImportDlg');
      this.syncedShowDialog = false;
    } catch (err) {
      // console.error("Error saving user certificate", err);
    } finally {
      this.busy = false;
    }
  }

  async onCancel() {
    clearAllBodyScrollLocks();
    if (this.userCertificate && this.userCertificate.id == 0 && this.userCertificate.pdfFileName) {
      // if closing date is not valid, set it to current date
      if (this.isValidDate) {
        this.userCertificate.closingDate = new Date();
      }
      await rest.url("orgAdmin/removeUnusedPdfCertificate").post(this.userCertificate);
    }
    this.syncedShowDialog = false;
  }
}
